import { Workflow, WorkflowStep, WorkflowDefinition, WorkflowTrigger, WorkflowStepType } from '../features/workflows/types/workflow';

// Gemini API configuration
const GEMINI_API_KEY = import.meta.env.VITE_GEMINI_API_KEY || 'AIzaSyCjJqcsXjZ9B3e1zqUunMh3q5DR6c1gk-E';

// Model endpoints in order of preference
const GEMINI_MODELS = [
  {
    name: 'Gemini 2.0 Flash',
    endpoint: `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${GEMINI_API_KEY}`,
    limits: { rpm: 15, tpm: 1000000, rpd: 1500 }
  },
  {
    name: 'Gemini 2.0 Flash-Lite',
    endpoint: `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash-lite:generateContent?key=${GEMINI_API_KEY}`,
    limits: { rpm: 30, tpm: 1000000, rpd: 1500 }
  }
];

export interface AIWorkflowRequest {
  description: string;
  objective?: string;
  targetAudience?: string;
  industry?: string;
  channels?: string[];
  timeline?: string;
  additionalContext?: string;
}

export interface AIWorkflowResponse {
  workflow: Workflow;
  reasoning: string;
  confidence: number;
  suggestions: string[];
  isSpecific: boolean; // true for specific requirements, false for general concepts
  modelUsed: string;
  isFallback?: boolean;
  originalError?: string;
}

export interface WorkflowGenerationContext {
  detectedIntent: 'specific' | 'general';
  extractedEntities: {
    actions: string[];
    timing: string[];
    conditions: string[];
    channels: string[];
  };
  complexity: 'simple' | 'medium' | 'complex';
}

class AIWorkflowService {
  private static rateLimitTracking = new Map<string, { count: number; resetTime: number }>();

  /**
   * Generate a workflow from natural language description
   */
  static async generateWorkflow(request: AIWorkflowRequest): Promise<AIWorkflowResponse> {
    try {
      // Analyze the request to determine if it's specific or general
      const context = await this.analyzeRequest(request);
      
      // Generate the workflow based on the context
      const prompt = this.buildWorkflowPrompt(request, context);
      const response = await this.callGeminiAPI(prompt);
      
      // Parse the AI response
      const parsedResponse = this.parseWorkflowResponse(response.content);
      
      // Create the workflow object
      const workflow = this.createWorkflowFromAI(parsedResponse, request);
      
      return {
        workflow,
        reasoning: parsedResponse.reasoning || 'Workflow generated based on your requirements',
        confidence: parsedResponse.confidence || 0.8,
        suggestions: parsedResponse.suggestions || [],
        isSpecific: context.detectedIntent === 'specific',
        modelUsed: response.modelUsed,
        isFallback: false,
        originalError: ''
      };
    } catch (error) {
      console.error('AI workflow generation failed:', error);
      
      // Create a fallback response with a basic workflow
      const fallbackWorkflow = this.createWorkflowFromAI(this.createFallbackWorkflow(), request);
      
      return {
        workflow: fallbackWorkflow,
        reasoning: 'Generated a basic workflow due to AI service issues. Please customize as needed.',
        confidence: 0.5,
        suggestions: [
          'Review and customize this workflow',
          'Add more specific steps based on your needs',
          'Configure step properties as required'
        ],
        isSpecific: false,
        modelUsed: 'fallback',
        isFallback: true,
        originalError: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Analyze the user request to understand intent and complexity
   */
  private static async analyzeRequest(request: AIWorkflowRequest): Promise<WorkflowGenerationContext> {
    const analysisPrompt = `
Analyze this workflow request and return ONLY valid JSON:

Description: "${request.description}"

Return analysis in this exact JSON format:
{
  "detectedIntent": "specific",
  "extractedEntities": {
    "actions": ["send email", "wait"],
    "timing": ["2 days", "immediately"],
    "conditions": ["if email opened"],
    "channels": ["email", "sms"]
  },
  "complexity": "medium"
}

Rules:
- detectedIntent: "specific" if exact steps mentioned, "general" if conceptual
- extractedEntities: arrays of relevant items found in description
- complexity: "simple" (1-2 steps), "medium" (3-5 steps), "complex" (6+ steps)

Return ONLY the JSON object.`;

    try {
      const response = await this.callGeminiAPI(analysisPrompt);
      const parsed = this.parseWorkflowResponse(response.content);
      
      // Validate the parsed response has required fields
      if (parsed.detectedIntent && parsed.extractedEntities && parsed.complexity) {
        return parsed;
      } else {
        throw new Error('Invalid analysis response structure');
      }
    } catch (error) {
      console.error('Analysis failed, using fallback:', error);
      return {
        detectedIntent: this.detectIntentFallback(request.description),
        extractedEntities: { actions: [], timing: [], conditions: [], channels: [] },
        complexity: 'medium'
      };
    }
  }

  /**
   * Build the main workflow generation prompt
   */
  private static buildWorkflowPrompt(request: AIWorkflowRequest, context: WorkflowGenerationContext): string {
    const isSpecific = context.detectedIntent === 'specific';
    
    return `
You are a workflow automation expert. Generate a workflow based on this request: "${request.description}"

CRITICAL JSON REQUIREMENTS:
- Return ONLY valid JSON. No explanations, no markdown, no code blocks
- Use double quotes for all strings and property names
- Do NOT escape quotes unless they are inside string values
- Use null instead of undefined
- Do NOT use trailing commas
- Ensure all brackets and braces are properly closed

Available step types: trigger, email, sms, delay, condition, webhook, api_call, notification

WORKFLOW FLOW RULES:
- Steps must be connected in sequence to create a proper workflow flow
- Each step (except the last) should connect to the next step
- Use "connections": [{"id": "connection_id", "sourceStepId": "current_step", "targetStepId": "next_step", "condition": null, "label": null}] to link steps together
- The workflow should have a logical progression from start to finish

Required JSON structure:
{
  "name": "Workflow Name",
  "description": "Brief description",
  "triggers": [
    {
      "type": "manual",
      "name": "Manual Trigger",
      "config": {}
    }
  ],
  "steps": [
    {
      "id": "step_1",
      "type": "email",
      "name": "Send Email",
      "description": "Send welcome email",
      "config": {
        "subject": "Welcome",
        "body": "Hello!"
      },
      "position": {
        "x": 100,
        "y": 100
      },
      "connections": [
        {
          "id": "connection_step_1_to_step_2",
          "sourceStepId": "step_1",
          "targetStepId": "step_2",
          "condition": null,
          "label": null
        }
      ],
      "conditions": [],
      "retryConfig": {
        "maxAttempts": 3,
        "delay": 1000
      }
    },
    {
      "id": "step_2",
      "type": "delay",
      "name": "Wait",
      "description": "Wait 24 hours",
      "config": {
        "duration": 86400000
      },
      "position": {
        "x": 200,
        "y": 100
      },
      "connections": [
        {
          "id": "connection_step_2_to_step_3",
          "sourceStepId": "step_2",
          "targetStepId": "step_3",
          "condition": null,
          "label": null
        }
      ],
      "conditions": [],
      "retryConfig": {
        "maxAttempts": 3,
        "delay": 1000
      }
    },
    {
      "id": "step_3",
      "type": "sms",
      "name": "Send SMS",
      "description": "Send follow-up SMS",
      "config": {
        "message": "Thanks for signing up!"
      },
      "position": {
        "x": 300,
        "y": 100
      },
      "connections": [],
      "conditions": [],
      "retryConfig": {
        "maxAttempts": 3,
        "delay": 1000
      }
    }
  ],
  "reasoning": "Explanation of workflow design",
  "confidence": 0.9,
  "suggestions": [
    "Add personalization",
    "Include error handling"
  ]
}

IMPORTANT: 
- Generate 2-5 steps with sequential connections
- Each step (except the last) should connect to the next step via "connections": [{"id": "connection_id", "sourceStepId": "current_step", "targetStepId": "next_step", "condition": null, "label": null}]
- Position steps with x coordinates 150 pixels apart (100, 250, 400, 550, 700) and y at 100 for a clean horizontal layout
- For conditional steps, position the "true" branch at y: 50 and "false" branch at y: 150
- The last step should have empty connections: []
- Connection IDs should follow the pattern: "connection_sourceId_to_targetId"

Return ONLY the JSON object.`;
  }

  /**
   * Parse the AI response into a structured format
   */
  private static parseWorkflowResponse(response: string): any {
    try {
      // First, try to parse the response directly
      const parsed = JSON.parse(response);
      return parsed;
    } catch (error) {
      // If that fails, try to extract JSON from the response
      const jsonMatch = response.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        console.error('No JSON found in AI response:', response.substring(0, 500));
        throw new Error('No JSON found in AI response');
      }
      
      let jsonString = jsonMatch[0];
      
      try {
        // Try to parse the extracted JSON
        const parsed = JSON.parse(jsonString);
        return parsed;
      } catch (parseError) {
        console.error('❌ JSON parse error:', parseError);
        
        // Try to fix common JSON issues
        jsonString = this.fixCommonJSONIssues(jsonString);
        
        try {
          const parsed = JSON.parse(jsonString);
          return parsed;
        } catch (finalError) {
          console.error('❌ Final JSON parse failed:', finalError);
          console.error('❌ Final JSON string (first 1000 chars):', jsonString.substring(0, 1000));
          
          // Last resort: try to create a minimal valid workflow from the broken JSON
          try {
            const fallbackFromBroken = this.extractWorkflowFromBrokenJSON(jsonString);
            if (fallbackFromBroken) {
              return fallbackFromBroken;
            }
          } catch (extractError) {
            console.error('❌ Could not extract from broken JSON:', extractError);
          }
          
          // Return a fallback workflow structure
          return this.createFallbackWorkflow();
        }
      }
    }
  }

  /**
   * Try to extract a valid workflow from broken JSON
   */
  private static extractWorkflowFromBrokenJSON(brokenJson: string): any | null {
    try {
      // Try to extract basic workflow information using regex
      const nameMatch = brokenJson.match(/"name":\s*"([^"]+)"/);
      const descMatch = brokenJson.match(/"description":\s*"([^"]+)"/);
      
      if (nameMatch || descMatch) {
        return {
          name: nameMatch?.[1] || "AI Generated Workflow",
          description: descMatch?.[1] || "A workflow generated by AI",
          triggers: [
            {
              type: "manual",
              name: "Manual Trigger",
              config: {}
            }
          ],
          steps: [
            {
              id: "step_1",
              type: "email",
              name: "Send Email",
              description: "Send an email notification",
              config: {
                subject: "Workflow Notification",
                body: "This is an automated message from your workflow."
              },
              position: { x: 100, y: 100 },
              connections: [
                {
                  id: "connection_step_1_to_step_2",
                  sourceStepId: "step_1",
                  targetStepId: "step_2",
                  condition: null,
                  label: null
                }
              ],
              conditions: [],
              retryConfig: { maxAttempts: 3, delay: 1000 }
            },
            {
              id: "step_2",
              type: "sms",
              name: "Send SMS",
              description: "Send an SMS follow-up",
              config: {
                message: "Follow-up message from your workflow."
              },
              position: { x: 250, y: 100 },
              connections: [],
              conditions: [],
              retryConfig: { maxAttempts: 3, delay: 1000 }
            }
          ],
          reasoning: "Extracted workflow from partially valid AI response",
          confidence: 0.6,
          suggestions: ["Review and customize this workflow", "Add more specific steps"]
        };
      }
    } catch (error) {
      console.error('Failed to extract from broken JSON:', error);
    }
    
    return null;
  }

  /**
   * Fix common JSON formatting issues
   */
  private static fixCommonJSONIssues(jsonString: string): string {
    // Remove any leading/trailing whitespace
    jsonString = jsonString.trim();
    
    // Fix escaped quotes that shouldn't be escaped (like "name\": should be "name":)
    jsonString = jsonString.replace(/([a-zA-Z_][a-zA-Z0-9_]*)\\":/g, '$1":');
    
    // Fix escaped quotes in string values that break the JSON
    jsonString = jsonString.replace(/:\s*"([^"]*)\\"([^"]*)"([,\s\}])/g, ': "$1\\"$2"$3');
    
    // Fix undefined values (JSON doesn't support undefined)
    jsonString = jsonString.replace(/:\s*undefined/g, ': null');
    
    // Fix trailing commas
    jsonString = jsonString.replace(/,(\s*[}\]])/g, '$1');
    
    // Fix single quotes to double quotes (but be careful not to break apostrophes)
    jsonString = jsonString.replace(/'/g, '"');
    
    // Fix double-escaped quotes
    jsonString = jsonString.replace(/\\"/g, '"');
    
    // Fix any remaining quote issues in property names
    jsonString = jsonString.replace(/([{,]\s*)([a-zA-Z_][a-zA-Z0-9_]*)\s*:/g, '$1"$2":');
    
    // Remove any non-printable characters
    jsonString = jsonString.replace(/[\x00-\x1F\x7F]/g, '');
    
    // Fix specific patterns we've seen in the error
    jsonString = jsonString.replace(/\\"([^"]*)\\":/g, '"$1":');
    jsonString = jsonString.replace(/\\"([^"]*)\\"([,\s\}])/g, '"$1"$2');
    
    return jsonString;
  }

  /**
   * Create a fallback workflow when JSON parsing fails
   */
  private static createFallbackWorkflow(): any {
    return {
      name: "AI Generated Workflow",
      description: "A basic workflow generated by AI",
      triggers: [
        {
          type: "manual",
          name: "Manual Trigger",
          config: {}
        }
      ],
      steps: [
        {
          id: "step_1",
          type: "email",
          name: "Send Welcome Email",
          description: "Send an email notification",
          config: {
            subject: "Welcome to our platform!",
            body: "Thank you for joining us. We're excited to have you on board!"
          },
          position: { x: 100, y: 100 },
          connections: [
            {
              id: "connection_step_1_to_step_2",
              sourceStepId: "step_1",
              targetStepId: "step_2",
              condition: null,
              label: null
            }
          ],
          conditions: [],
          retryConfig: { maxAttempts: 3, delay: 1000 }
        },
        {
          id: "step_2",
          type: "delay",
          name: "Wait 24 Hours",
          description: "Wait before sending follow-up",
          config: {
            duration: 86400000
          },
          position: { x: 250, y: 100 },
          connections: [
            {
              id: "connection_step_2_to_step_3",
              sourceStepId: "step_2",
              targetStepId: "step_3",
              condition: null,
              label: null
            }
          ],
          conditions: [],
          retryConfig: { maxAttempts: 3, delay: 1000 }
        },
        {
          id: "step_3",
          type: "sms",
          name: "Send Follow-up SMS",
          description: "Send an SMS follow-up message",
          config: {
            message: "Thanks for joining! Let us know if you have any questions."
          },
          position: { x: 400, y: 100 },
          connections: [],
          conditions: [],
          retryConfig: { maxAttempts: 3, delay: 1000 }
        }
      ],
      reasoning: "Fallback workflow created due to AI response parsing issues",
      confidence: 0.5,
      suggestions: ["Review and customize this workflow", "Add more specific steps", "Configure step properties as needed"]
    };
  }

  /**
   * Ensure steps have proper connections and positioning
   */
  private static ensureStepConnections(steps: any[]): any[] {
    if (!steps || steps.length === 0) return steps;

    // Create a copy of steps to avoid mutation
    const processedSteps = steps.map(step => ({ ...step }));

    // Ensure proper positioning with 150px spacing
    for (let i = 0; i < processedSteps.length; i++) {
      const step = processedSteps[i];
      
      // Ensure position exists with proper spacing
      if (!step.position) {
        step.position = { x: 100 + (i * 150), y: 100 };
      } else {
        // Adjust x position to ensure proper spacing
        step.position.x = 100 + (i * 150);
        // Keep existing y position or default to 100
        if (!step.position.y) {
          step.position.y = 100;
        }
      }
    }

    // Ensure each step (except the last) connects to the next step
    for (let i = 0; i < processedSteps.length - 1; i++) {
      const currentStep = processedSteps[i];
      const nextStep = processedSteps[i + 1];

      // Initialize connections as proper WorkflowConnection objects
      if (!currentStep.connections || !Array.isArray(currentStep.connections)) {
        currentStep.connections = [];
      }

      // Check if connection already exists (could be string ID or connection object)
      const existingConnection = currentStep.connections.find((conn: any) => {
        if (typeof conn === 'string') {
          return conn === nextStep.id;
        }
        return conn.targetStepId === nextStep.id;
      });

      if (!existingConnection) {
        // Create a proper WorkflowConnection object
        const newConnection = {
          id: `connection_${currentStep.id}_to_${nextStep.id}`,
          sourceStepId: currentStep.id,
          targetStepId: nextStep.id,
          condition: null,
          label: null
        };
        
        currentStep.connections.push(newConnection);
      } else {
        // If it's a string, convert it to a proper connection object
        if (typeof existingConnection === 'string') {
          const connectionIndex = currentStep.connections.indexOf(existingConnection);
          currentStep.connections[connectionIndex] = {
            id: `connection_${currentStep.id}_to_${nextStep.id}`,
            sourceStepId: currentStep.id,
            targetStepId: nextStep.id,
            condition: null,
            label: null
          };
        }
      }
    }

    // Ensure the last step has empty connections
    if (processedSteps.length > 0) {
      const lastStep = processedSteps[processedSteps.length - 1];
      if (!lastStep.connections || !Array.isArray(lastStep.connections)) {
        lastStep.connections = [];
      }
    }

    return processedSteps;
  }

  /**
   * Validate workflow connections and structure
   */
  private static validateWorkflowStructure(steps: any[]): { isValid: boolean; issues: string[] } {
    const issues: string[] = [];
    
    if (!steps || steps.length === 0) {
      issues.push('Workflow has no steps');
      return { isValid: false, issues };
    }

    // Check for duplicate step IDs
    const stepIds = steps.map(step => step.id);
    const duplicateIds = stepIds.filter((id, index) => stepIds.indexOf(id) !== index);
    if (duplicateIds.length > 0) {
      issues.push(`Duplicate step IDs found: ${duplicateIds.join(', ')}`);
    }

    // Check connections validity
    for (let i = 0; i < steps.length; i++) {
      const step = steps[i];
      
      if (!step.id) {
        issues.push(`Step at index ${i} is missing an ID`);
        continue;
      }

      if (!step.connections || !Array.isArray(step.connections)) {
        issues.push(`Step ${step.id} has invalid connections`);
        continue;
      }

      // Check if connections reference valid step IDs
      for (const connection of step.connections) {
        if (typeof connection === 'string') {
          // Handle legacy string connections
          if (!stepIds.includes(connection)) {
            issues.push(`Step ${step.id} connects to non-existent step: ${connection}`);
          }
        } else if (connection.targetStepId) {
          // Handle proper WorkflowConnection objects
          if (!stepIds.includes(connection.targetStepId)) {
            issues.push(`Step ${step.id} connects to non-existent step: ${connection.targetStepId}`);
          }
        } else {
          issues.push(`Step ${step.id} has invalid connection object: ${JSON.stringify(connection)}`);
        }
      }

      // Check if non-last steps have connections (after our processing, they should)
      if (i < steps.length - 1 && step.connections.length === 0) {
        issues.push(`Step ${step.id} should connect to the next step but has no connections`);
      }

      // Check if last step has no connections
      if (i === steps.length - 1 && step.connections.length > 0) {
        const connectionTargets = step.connections.map((conn: any) => 
          typeof conn === 'string' ? conn : conn.targetStepId
        );
        issues.push(`Last step ${step.id} should not have connections but connects to: ${connectionTargets.join(', ')}`);
      }
    }

    return { isValid: issues.length === 0, issues };
  }

  /**
   * Create a Workflow object from AI response
   */
  private static createWorkflowFromAI(aiResponse: any, request: AIWorkflowRequest): Workflow {
    const now = new Date();
    
    // Ensure steps have proper connections
    const processedSteps = this.ensureStepConnections(aiResponse.steps || []);
    
    // Validate the workflow structure
    const validation = this.validateWorkflowStructure(processedSteps);
    if (!validation.isValid) {
      console.warn('Workflow validation issues:', validation.issues);
    }
    
    const workflow = {
      id: `workflow_${Date.now()}`,
      name: aiResponse.name || 'AI Generated Workflow',
      description: aiResponse.description || request.description,
      definition: {
        steps: processedSteps,
        variables: aiResponse.variables || [],
        settings: {
          maxExecutionTime: 3600000, // 1 hour
          maxRetries: 3,
          errorHandling: 'retry' as const,
          notifications: {
            onSuccess: true,
            onError: true,
            recipients: []
          }
        }
      },
      triggers: aiResponse.triggers || [],
      isActive: false,
      status: 'draft' as const,
      createdBy: 'ai_assistant',
      createdAt: now,
      updatedAt: now,
      executionCount: 0,
      successRate: 0,
      metadata: {
        generatedBy: 'ai',
        originalRequest: request,
        confidence: aiResponse.confidence || 0.8,
        validationResult: validation
      }
    };
    
    return workflow;
  }

  /**
   * Call Gemini API with fallback models
   */
  private static async callGeminiAPI(prompt: string): Promise<{ content: string; modelUsed: string }> {
    for (const model of GEMINI_MODELS) {
      try {
        const response = await fetch(model.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contents: [{ parts: [{ text: prompt }] }],
            generationConfig: {
              temperature: 0.7,
              maxOutputTokens: 8192
            }
          })
        });

        if (response.ok) {
          const data = await response.json();
          const content = data.candidates?.[0]?.content?.parts?.[0]?.text || '';
          return { content, modelUsed: model.name };
        }
      } catch (error) {
        console.error(`${model.name} failed:`, error);
      }
    }
    throw new Error('All Gemini models failed');
  }

  /**
   * Fallback intent detection
   */
  private static detectIntentFallback(description: string): 'specific' | 'general' {
    const specificKeywords = ['then', 'wait', 'after', 'delay', 'next', 'step 1'];
    return specificKeywords.some(k => description.toLowerCase().includes(k)) ? 'specific' : 'general';
  }

  /**
   * Generate workflow suggestions based on industry and use case
   */
  static async generateSuggestions(industry?: string): Promise<string[]> {
    try {
      const prompt = `Generate 5 workflow suggestions for ${industry || 'business'} industry. JSON array format.`;
      const response = await this.callGeminiAPI(prompt);
      return JSON.parse(response.content);
    } catch {
      return ['Automate customer onboarding', 'Lead nurturing sequence', 'Follow-up workflows'];
    }
  }

  /**
   * Optimize an existing workflow
   */
  static async optimizeWorkflow(workflow: Workflow): Promise<{
    optimizedWorkflow: Workflow;
    improvements: string[];
    reasoning: string;
  }> {
    const prompt = `
      Analyze and optimize this workflow:
      
      ${JSON.stringify(workflow, null, 2)}
      
      Provide optimizations for:
      1. Performance improvements
      2. Error handling enhancements
      3. User experience improvements
      4. Best practice implementations
      
      Return the optimized workflow and list of improvements made.
      
      Format as JSON:
      {
        "optimizedWorkflow": { /* full workflow object */ },
        "improvements": ["improvement 1", "improvement 2"],
        "reasoning": "explanation of changes"
      }
    `;

    try {
      const response = await this.callGeminiAPI(prompt);
      const result = JSON.parse(response.content);
      
      return {
        optimizedWorkflow: result.optimizedWorkflow || workflow,
        improvements: result.improvements || [],
        reasoning: result.reasoning || 'Workflow analyzed for optimization opportunities'
      };
    } catch (error) {
      console.error('Failed to optimize workflow:', error);
      throw new Error('Workflow optimization failed');
    }
  }
}

export default AIWorkflowService; 